import React, { createContext, useContext, useState, useCallback } from 'react'

type Toast = { id: number; text: string; kind?: 'info'|'error'|'success' }

const ToastCtx = createContext<{ push: (t: Omit<Toast,'id'>) => void }|null>(null)

export function useToasts() {
  try {
    const ctx = useContext(ToastCtx)
    return ctx ?? { push: () => {} }
  } catch {
    return { push: () => {} }
  }
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const push = useCallback((t: Omit<Toast,'id'>) => {
    const id = Date.now() + Math.random()
    setToasts(prev => [...prev, { id, ...t }])
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 3000)
  }, [])
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="fixed right-4 bottom-4 flex flex-col gap-2 z-50" role="status" aria-live="polite">
        {toasts.map(t => (
          <div
            key={t.id}
            className={
              'px-3 py-2 rounded shadow border text-sm flex items-center gap-2 ' +
              (t.kind === 'error'
                ? 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800'
                : t.kind === 'success'
                ? 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800'
                : 'bg-gray-50 text-gray-800 border-gray-200 dark:bg-gray-900/60 dark:text-gray-200 dark:border-gray-700')
            }
          >
            <span aria-hidden="true">
              {t.kind === 'error' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 14h-2v-2h2v2Zm0-4h-2V6h2v6Z"/></svg>
              ) : t.kind === 'success' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 14-4-4 1.4-1.4L11 12.2l4.6-4.6L17 9l-6 7Z"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15h-2v-2h2v2Zm0-4h-2V7h2v6Z"/></svg>
              )}
            </span>
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}
